import { useState } from 'react';
import { IonSpinner } from '@ionic/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAddressCapture } from '../../hooks/useAddressCapture';
import { useAutoCaptureOnPermissionGrant } from '../../hooks/useAutoCaptureOnPermissionGrant';
import { useAuthStore } from '../../store/useAuthStore';
import { useDisplayProfile } from '../../hooks/useDisplayProfile';
import { Avatar } from '../ui/Avatar';
import { SignInModal } from '../auth/SignInModal';
import { LanguageSwitcher } from '../profile/LanguageSwitcher';
import { LocaleSearchInput } from './LocaleSearchInput';

/**
 * Full-screen location gate rendered on the home route when the user has no
 * sufficient location yet. We no longer have a separate /location-required page
 * — this lives inline on the home feed — but it restores the old gate's look:
 * food hero + wordmark, "Where are you?", the tax/visibility explainer, then the
 * capture controls (device geolocation, or a city/ZIP search). HomePage hides the top bar in
 * both auth states so this reads as a standalone welcome screen; the top-right
 * corner slot shows the Sign In button when anon and the profile avatar
 * (linking to /profile) when authed.
 *
 * Capture logic lives in `useAddressCapture`; once an address resolves,
 * `InitLocale` reactively resolves the locale and the feed loads in place (no
 * navigation). Mirrors a real street address to the profile.
 */
export const InlineLocationPrompt: React.FC = () => {
  // `home` carries the title/body; the capture controls ("Use my current
  // location", "or", search label) reuse the `location` namespace, and Sign In /
  // Continue reach into `common`.
  const { t } = useTranslation(['home', 'location']);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Optimistic-paint snapshot — null pre-whoami when not signed in, resolves
  // to a cached profile (avatar + name) when signed in so the corner avatar
  // shows on first paint without waiting for the whoami round-trip.
  const displayProfile = useDisplayProfile();
  const [signInOpen, setSignInOpen] = useState(false);
  const {
    query,
    setQuery,
    geoLoading,
    geoError,
    searchError,
    handleUseDeviceLocation,
    handleSearchSubmit,
    searchSubmitting,
  } = useAddressCapture({ usOnly: true });

  // This gate is only mounted while the user has no sufficient location, so it's
  // always "active". Turning browser location on here (native prompt / site
  // settings) captures through the same flow and the feed loads in place.
  useAutoCaptureOnPermissionGrant(true, handleUseDeviceLocation);

  return (
    <div data-testid="inline-location-prompt" className="flex min-h-full w-full flex-col">
      {/* Corner affordances — language switcher upper-left (both auth
          states; self-hides on a single-language config), Sign In when anon
          / profile avatar (links to /profile) when authed on the right.
          In-flow row (not absolute) so the controls never overlay the food
          hero on tight viewports; the body below uses `flex-1 justify-center`
          to absorb the remaining space and keeps the headline centered. The
          right wrapper renders in all auth states (including the brief
          authed-but-snapshot-loading window) so the body's Y position doesn't
          shift when the avatar hydrates. The left wrapper always renders too
          so `justify-between` keeps the right affordance pinned right even
          when the switcher is hidden. */}
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="flex">
          <LanguageSwitcher />
        </div>
        {isAuthenticated && displayProfile ? (
          <Link to="/profile" className="flex" data-testid="profile-avatar">
            <Avatar
              src={displayProfile.avatarUrl}
              name={displayProfile.displayName}
              size="md"
              className="!h-10 !w-10"
            />
          </Link>
        ) : !isAuthenticated ? (
          // Solid brand-primary button — the prominent, filled corner CTA.
          // Corner-sized (px-4 py-2) so it stays lighter-weight than the body's
          // full-width location CTA while still reading as a primary action.
          <button
            type="button"
            data-testid="sign-in-button"
            onClick={() => setSignInOpen(true)}
            className="shrink-0 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white active:opacity-80"
          >
            {t('common:actions.signIn')}
          </button>
        ) : null}
      </div>

      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-6 px-6 pb-10 pt-4 [@media(max-height:700px)]:gap-4 [@media(max-height:700px)]:pb-6 [@media(max-height:700px)]:pt-2">
        <img
          src="/images/Any_Food-300w.png"
          alt=""
          className="mx-auto w-[300px] max-w-full [@media(max-height:640px)]:hidden"
        />
        <img
          src="/images/HomePlatedTM.svg"
          alt="HomePlated"
          className="mx-auto w-[300px] max-w-full"
        />

        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold">{t('home:feed.locationPrompt.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('home:feed.locationPrompt.body')}</p>
        </div>

        <div className="space-y-2">
          <button
            type="button"
            data-testid="use-device-location"
            onClick={handleUseDeviceLocation}
            disabled={geoLoading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
          >
            {geoLoading ? (
              <>
                <IonSpinner name="crescent" className="h-5 w-5" />
                {t('location:gettingLocation')}
              </>
            ) : (
              t('location:useDeviceButton')
            )}
          </button>
          {geoError && (
            <p data-testid="location-error" className="text-center text-sm text-brand-danger">
              {geoError}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('location:divider.or')}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <LocaleSearchInput
          inputId="home-location-search"
          value={query}
          onChange={setQuery}
          onSubmit={handleSearchSubmit}
          submitting={searchSubmitting}
          error={searchError}
          label={t('location:search.label')}
          placeholder={t('location:search.placeholder')}
          ctaLabel={t('common:actions.continue')}
          testIds={{
            input: 'location-input',
            submit: 'location-input-submit',
            error: 'location-error',
          }}
        />
      </div>

      <SignInModal isOpen={signInOpen} onClose={() => setSignInOpen(false)} />
    </div>
  );
};
